import axios from 'axios'
import {FC, FormEvent, ReactNode, useEffect, useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {API_URL, GetBrowserData} from '../../../../_metronic/helpers'
import {toPersianDigits} from '../../../../_metronic/helpers/components/datepicker/persianDateValue'
import {useAuth} from '../core/Auth'
import {AuthModel} from '../core/_models'
import {
  ACCEPT_PERSONAL_USE_FIRST,
  CompletionMoment,
  readCompletionError,
} from '../core/accountCompletion'
import {accountDisabledMessage} from '../core/accountDisabled'
import {wrapCredential} from '../core/credentialCipher'
import {clearCredentialNotice} from '../core/credentialNotice'
import {getEntryPath} from '../core/postLoginPath'
import {AccountEntryLayout} from './AccountEntryLayout'
import {CredentialFields, CredentialValues, credentialProblems} from './CredentialFields'
import {PersonalUseNotice} from './PersonalUseNotice'
import './AccountCompletion.css'

export type SignInLinkInfo = {
  firstName?: string | null
  username?: string | null
  hasPassword: boolean
  mobile?: string | null
  expiresAt?: CompletionMoment | null
}

const LINK_CODES = ['LINK_EXPIRED', 'LINK_USED', 'LINK_INVALID']

/** The token of the texted link: after «#», so it never reaches a server log or a Referer; read once, then dropped. */
export const readLinkToken = (): string => {
  const token = decodeURIComponent((window.location.hash || '').replace(/^#/, '')).trim()
  if (token) {
    try {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search
      )
    } catch {
      // The address keeps the token until the next page; nothing else depends on it.
    }
  }
  return token
}

type Props = {
  /** Opened inside the panel by someone signed in: the page brings its own frame (signed out, AuthPage frames it). */
  standalone?: boolean
}

/**
 * «تعیین نام کاربری و رمز عبور» from «ارسال لینک ورود»: the one-time link texted to the user's confirmed mobile opens
 * here; the user sets a username and a new password and is signed in with them (other sessions are closed).
 */
const SignInLinkPage: FC<Props> = ({standalone = false}) => {
  const {saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const tokenRef = useRef<string>()
  if (tokenRef.current === undefined) tokenRef.current = readLinkToken()
  const token = tokenRef.current

  const [info, setInfo] = useState<SignInLinkInfo>()
  const [linkProblem, setLinkProblem] = useState(
    token ? '' : 'این لینک کامل نیست؛ همه‌ی لینکِ پیامک را باز کنید یا دوباره لینک بخواهید.'
  )
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(Boolean(token))
  const [values, setValues] = useState<CredentialValues>({username: '', password: '', repeat: ''})
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [usernameTaken, setUsernameTaken] = useState('')
  // «این تعهد را خواندم و می‌پذیرم», as on «تکمیل حساب کاربری»: the save is not sent before it is ticked.
  const [accepted, setAccepted] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)

  const open = async () => {
    if (!token) return
    setLoading(true)
    setLoadError('')
    try {
      const {data} = await axios.post<SignInLinkInfo>(`${API_URL}/auth/link/open`, {token})
      setInfo(data)
      setValues((current) => ({...current, username: data.username || ''}))
    } catch (reason) {
      const failure = readCompletionError(reason, 'لینک بررسی نشد. دوباره تلاش کنید.')
      if (failure.status === 410 || LINK_CODES.includes(failure.code || ''))
        setLinkProblem(failure.message)
      else setLoadError(failure.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    open()
    // The token is read once, when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loading) headingRef.current?.focus()
  }, [loading, linkProblem])

  const problems = credentialProblems(values, info?.username, info?.mobile)
  const invalid = Boolean(problems.username || problems.password || problems.repeat)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (saving || invalid || !info || !accepted) return
    setSaving(true)
    setError('')
    setUsernameTaken('')
    try {
      const browser = await GetBrowserData()
      const {data: user} = await axios.post<AuthModel>(`${API_URL}/auth/link/complete`, {
        token,
        username: wrapCredential(values.username.trim()),
        password: wrapCredential(values.password),
        browser,
        acceptPersonalUse: true,
      })
      clearCredentialNotice()
      saveAuth(user)
      setCurrentUser(user)
      navigate(getEntryPath(user), {replace: true})
    } catch (reason) {
      const disabled = accountDisabledMessage(reason)
      const failure = readCompletionError(reason, 'نام کاربری و رمز ذخیره نشد. دوباره تلاش کنید.')
      if (disabled) setLinkProblem(disabled)
      else if (failure.status === 410 || LINK_CODES.includes(failure.code || ''))
        setLinkProblem(failure.message)
      else if (failure.code === 'USERNAME_TAKEN') setUsernameTaken(failure.message)
      else setError(failure.message)
      setSaving(false)
    }
  }

  let content: ReactNode
  if (loading) {
    content = (
      <div className='account-entry__center' role='status'>
        <span className='spinner-border spinner-border-sm' aria-hidden='true' /> در حال بررسی لینک…
      </div>
    )
  } else if (linkProblem) {
    content = (
      <>
        <div className='auth-message auth-message--warning' role='alert'>
          <i className='las la-unlink' aria-hidden='true' />
          <span>{linkProblem}</span>
        </div>
        <p className='text-gray-700 fs-6'>
          هر لینک فقط یک بار و تا زمان اعلام‌شده کار می‌کند. می‌توانید با شماره موبایل وارد شوید و
          نام کاربری و رمز را در «حساب کاربری › امنیت و ورود» تعیین کنید، یا از مدیر کسب‌وکار لینک
          تازه بخواهید.
        </p>
        <div className='account-entry__actions'>
          <Link to='/auth/loginMobile' className='btn btn-primary'>
            <i className='las la-mobile-alt' aria-hidden='true' /> ورود با شماره موبایل
          </Link>
          <Link to='/auth/login' className='btn btn-light'>
            <i className='las la-user' aria-hidden='true' /> ورود با نام کاربری
          </Link>
        </div>
      </>
    )
  } else if (loadError || !info) {
    content = (
      <div className='auth-message auth-message--danger' role='alert'>
        <i className='las la-exclamation-circle' aria-hidden='true' />
        <div className='flex-grow-1'>
          <span className='d-block'>{loadError || 'لینک بررسی نشد.'}</span>
          <button type='button' className='btn btn-sm btn-light-danger mt-2' onClick={open}>
            تلاش مجدد
          </button>
        </div>
      </div>
    )
  } else {
    content = (
      <form onSubmit={submit} noValidate>
        <PersonalUseNotice accepted={accepted} onChange={setAccepted} disabled={saving} />
        {error && (
          <div className='auth-message auth-message--danger' role='alert'>
            <i className='las la-exclamation-circle' aria-hidden='true' />
            <span>{error}</span>
          </div>
        )}
        <CredentialFields
          idPrefix='sign-in-link'
          values={values}
          onChange={(next) => {
            setValues(next)
            setUsernameTaken('')
          }}
          currentUsername={info.username}
          mobile={info.mobile}
          showErrors={touched}
          usernameServerError={usernameTaken}
          disabled={saving}
        />
        {!accepted && <p className='account-entry__blocked'>{ACCEPT_PERSONAL_USE_FIRST}</p>}
        <button
          type='submit'
          className='btn btn-primary auth-submit'
          disabled={saving || !accepted}
        >
          {saving ? (
            <span>
              <span className='spinner-border spinner-border-sm align-middle ms-2' /> در حال ذخیره…
            </span>
          ) : (
            <span>
              ذخیره و ورود <i className='las la-arrow-left' aria-hidden='true' />
            </span>
          )}
        </button>
        <p className='auth-security-note'>
          <i className='las la-shield-alt' aria-hidden='true' /> پس از ذخیره، دیگر دستگاه‌هایی که با
          این حساب وارد شده‌اند خارج می‌شوند.
        </p>
      </form>
    )
  }

  const page = (
    <div className='mobile-login-flow'>
      <div className='auth-flow-head'>
        <h1 className='auth-title' ref={headingRef} tabIndex={-1}>
          تعیین نام کاربری و رمز عبور
        </h1>
        <p>
          {info?.firstName ? `${info.firstName} عزیز، ` : ''}
          نام کاربری و رمز عبور شخصی خودتان را تعیین کنید؛ با ذخیره، وارد پنل می‌شوید.
          {info?.mobile && (
            <>
              {' '}
              حساب: <bdi dir='ltr'>{toPersianDigits(info.mobile)}</bdi>
            </>
          )}
          {info?.expiresAt && (
            <>
              {' '}
              (لینک تا {info.expiresAt.day} ساعت {info.expiresAt.time} معتبر است.)
            </>
          )}
        </p>
      </div>
      {content}
    </div>
  )

  return standalone ? <AccountEntryLayout>{page}</AccountEntryLayout> : page
}

export {SignInLinkPage}
export default SignInLinkPage
